/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BookOpen, Search, Plus, Edit, Trash2 } from 'lucide-react';
import { Prompt } from '../../../types/prompts';
import { getPrompts, savePrompt, deletePrompt } from '../../../services/promptService';
import { PromptEditorModal } from './PromptEditorModal';

interface PromptLibraryModalProps {
    onClose: () => void;
    onSelectPrompt: (promptText: string) => void;
}

export const PromptLibraryModal: React.FC<PromptLibraryModalProps> = ({ onClose, onSelectPrompt }) => {
    const [prompts, setPrompts] = useState<Prompt[]>([]);
    const [search, setSearch] = useState('');
    const [editingPrompt, setEditingPrompt] = useState<Prompt | undefined | null>(null); // null for new, Prompt for edit

    useEffect(() => {
        setPrompts(getPrompts());
    }, []);

    const handleSave = (promptData: Omit<Prompt, 'id' | 'isCustom'> & { id?: string }) => {
        savePrompt(promptData);
        setPrompts(getPrompts()); // Refresh list
        setEditingPrompt(null);
    };

    const handleDelete = (id: string) => {
        if (window.confirm('Are you sure you want to delete this prompt?')) {
            deletePrompt(id);
            setPrompts(getPrompts()); // Refresh list
        }
    };

    const filteredPrompts = useMemo(() => {
        return prompts.filter(p => p.title.toLowerCase().includes(search.toLowerCase()) || p.text.toLowerCase().includes(search.toLowerCase()));
    }, [prompts, search]);

    const groupedPrompts = useMemo(() => {
        const groups: Record<string, Prompt[]> = { 'My Prompts': [], 'Analytics': [], 'Transactions': [], 'Management': [], 'General': [] };
        filteredPrompts.forEach(p => {
            if (p.isCustom) {
                groups['My Prompts'].push(p);
            } else if (groups[p.category]) {
                groups[p.category].push(p);
            } else {
                groups['General'].push(p);
            }
        });
        return groups;
    }, [filteredPrompts]);
    
    return (
        <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
                <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }} onClick={e => e.stopPropagation()} className="bg-white dark:bg-[#15171C] rounded-2xl shadow-2xl w-full max-w-2xl h-[80vh] flex flex-col border border-stone-200 dark:border-[#2D3142]">
                    <div className="p-4 border-b border-stone-100 dark:border-white/5 flex justify-between items-center shrink-0">
                        <div className="flex items-center gap-3">
                            <BookOpen size={20} className="text-nobel-gold" />
                            <h2 className="font-serif text-xl text-stone-900 dark:text-white">Prompt Library</h2>
                        </div>
                        <button onClick={onClose} className="text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors"><X size={20} /></button>
                    </div>
                    <div className="p-4 border-b border-stone-100 dark:border-white/5 shrink-0">
                        <div className="relative">
                            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search prompts..." className="w-full pl-10 pr-4 py-2 bg-stone-50 dark:bg-white/5 rounded-lg border border-stone-200 dark:border-white/10 focus:border-nobel-gold outline-none"/>
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar">
                        {Object.entries(groupedPrompts).map(([category, promptsInCategory]) => {
                            // @google/genai-fix: Cast promptsInCategory to Prompt[] to resolve TypeScript error where it was being inferred as `unknown`.
                            const prompts = promptsInCategory as Prompt[];
                            return (
                                prompts.length > 0 && (
                                    <div key={category}>
                                        <h3 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2 px-1">{category}</h3>
                                        <div className="space-y-2">
                                            {prompts.map(p => (
                                                <div key={p.id} className="group flex items-center gap-2 p-3 rounded-lg bg-stone-50 dark:bg-white/5 hover:bg-stone-100 dark:hover:bg-white/10 border border-stone-100 dark:border-white/10">
                                                    <div className="flex-1">
                                                        <div className="font-bold text-sm text-stone-900 dark:text-white">{p.title}</div>
                                                        <p className="text-xs text-stone-500 line-clamp-1">{p.text}</p>
                                                    </div>
                                                    {p.isCustom && (
                                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <button onClick={() => setEditingPrompt(p)} className="p-2 text-stone-400 hover:text-blue-500"><Edit size={14}/></button>
                                                            <button onClick={() => handleDelete(p.id)} className="p-2 text-stone-400 hover:text-red-500"><Trash2 size={14}/></button>
                                                        </div>
                                                    )}
                                                    <button onClick={() => { onSelectPrompt(p.text); onClose(); }} className="px-3 py-1.5 text-xs font-bold bg-stone-900 dark:bg-white text-white dark:text-black rounded-md hover:opacity-80">Use</button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )
                            );
                        })}
                    </div>
                    <div className="p-4 border-t border-stone-100 dark:border-white/5 shrink-0">
                        <button onClick={() => setEditingPrompt(null)} className="w-full flex items-center justify-center gap-2 p-3 text-sm font-bold bg-stone-100 dark:bg-white/5 hover:bg-stone-200 dark:hover:bg-white/10 rounded-lg">
                            <Plus size={16}/> Create New Prompt
                        </button>
                    </div>
                </motion.div>
            </motion.div>
            <AnimatePresence>
                {editingPrompt !== null && <PromptEditorModal prompt={editingPrompt || undefined} onClose={() => setEditingPrompt(null)} onSave={handleSave} />}
            </AnimatePresence>
        </>
    );
};